import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Button, getPrimitive} from '@tickchak/react-share/app';

import * as css from './VideoRatingPopup.css';
import RatingChart from './RatingChart';
import ThumbUp from '../../../assets/icons/ThumbUp';
import Comments from '../../../assets/icons/Comments';
import Pencil from '../../../assets/icons/Pencil';
import {VideoRatingData} from './types';
import {exportVideoCommentsToExcel} from './exportVideoComments';

interface VideoRatingRowProps {
    video: VideoRatingData;
    onEdit: (videoId: number) => void;
    index: number;
}

function VideoRatingRow({video, onEdit}: VideoRatingRowProps) {
    const {t} = useTranslation('layout');
    const lang = useSelector((state: any) => state.user?.lang) || 'he';
    const greenColor = getPrimitive('greens.green400');
    const redColor = getPrimitive('reds.red400');

    const handleViewComments = useCallback(async () => {
        await exportVideoCommentsToExcel(video.id, t, lang);
    }, [t, lang]);

    return (
        <css.tableRow>
            {/* Video Name */}
            <css.videoNameCell>
                <css.mobileLabel>{t('guideName')}</css.mobileLabel>
                <css.videoName title={video.title}>
                    {video.title}
                </css.videoName>
            </css.videoNameCell>

            {/* Scores Section - Mobile Label */}
            <css.mobileScoresLabel>{t('scores')}</css.mobileScoresLabel>

            {/* Scores Wrapper - keeps scores side by side on mobile */}
            <css.scoresWrapper>
                {/* Green Score */}
                <css.scoreCell>
                    <css.scoreCellLabel>
                        <ThumbUp fill="white" stroke={greenColor} />
                        <span>{t('greenScore')}</span>
                    </css.scoreCellLabel>
                    <css.scoreBadge $variant="green">
                        <css.scoreNumber $variant="green">
                            {video.greenScore}
                        </css.scoreNumber>
                        <css.scorePercent $variant="green">
                            ({video.greenPercent}%)
                        </css.scorePercent>
                    </css.scoreBadge>
                </css.scoreCell>

                {/* Red Score */}
                <css.scoreCell>
                    <css.scoreCellLabel>
                        <css.rotatedIcon>
                            <ThumbUp fill="white" stroke={redColor} />
                        </css.rotatedIcon>
                        <span>{t('redScore')}</span>
                    </css.scoreCellLabel>
                    <css.scoreBadge $variant="red">
                        <css.scoreNumber $variant="red">
                            {video.redScore}
                        </css.scoreNumber>
                        <css.scorePercent $variant="red">
                            ({video.redPercent}%)
                        </css.scorePercent>
                    </css.scoreBadge>
                </css.scoreCell>
            </css.scoresWrapper>

            {/* Mobile Section Label */}
            <css.mobileSectionLabel>{t('chartAndComments')}</css.mobileSectionLabel>

            {/* Chart and Comments Wrapper - keeps them side by side on mobile */}
            <css.chartCommentsWrapper>
                {/* Distribution Chart */}
                <css.chartCell>
                    <css.chartCellLabel>{t('distribution')}</css.chartCellLabel>
                    <RatingChart 
                        greenPercent={video.greenPercent}
                        redPercent={video.redPercent}
                    />
                </css.chartCell>

                {/* Comments Count */}
                <css.commentsCountCell>
                    <css.chartCellLabel>{t('comments')}</css.chartCellLabel>
                    <css.commentsCount>
                        {video.commentsCount}
                    </css.commentsCount>
                </css.commentsCountCell>
            </css.chartCommentsWrapper>

            {/* Views Count */}
            <css.viewsCountCell>
                <css.mobileLabel>{t('views')}</css.mobileLabel>
                <css.viewsCount>
                    {video.viewCount ?? 0}
                </css.viewsCount>
            </css.viewsCountCell>

            {/* Mobile Section Label */}
            <css.mobileSectionLabel>{t('actions')}</css.mobileSectionLabel>

            {/* Actions */}
            <css.actionsCell>
                <Button 
                    variant="brand"
                    size="category"
                    onClick={() => onEdit(video.id)}
                    iconStart={<Pencil />}
                >
                    {t('edit')}
                </Button>
                <Button 
                    variant="primary"
                    size="category"
                    onClick={handleViewComments}
                    iconStart={<Comments />}
                >
                    {t('comments')}
                </Button>
            </css.actionsCell>
        </css.tableRow>
    );
}

export default VideoRatingRow;
